import { useEffect, useState } from "react";
import { toast } from "@fox1ck-org/ui/toast";
import { orgUnitsApi } from "@/api/org-units";
import { ApiError } from "@/lib/api-error";
import {
  useAssignMemberAppRoles,
  useRemoveMemberAppRoles,
} from "@/hooks/use-org-units";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Skeleton, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@fox1ck-org/ui";
import type { AppRoleBinding, OrgUnitMember } from "@/types/org";

interface MemberRolesDialogProps {
  unitId: string;
  member: OrgUnitMember;
  appBindings: AppRoleBinding[];
  onClose: () => void;
}

export function MemberRolesDialog({
  unitId,
  member,
  appBindings,
  onClose,
}: MemberRolesDialogProps) {
  const assignRoles = useAssignMemberAppRoles(unitId);
  const removeRoles = useRemoveMemberAppRoles(unitId);
  const [memberRoles, setMemberRoles] = useState<Record<string, Set<string>>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadRoles() {
      const result: Record<string, Set<string>> = {};
      for (const binding of appBindings) {
        try {
          const data = await orgUnitsApi.getMemberAppRoles(unitId, member.id, binding.clientId);
          result[binding.clientId] = new Set(data.roles.map((r) => r.name));
        } catch {
          result[binding.clientId] = new Set();
        }
      }
      if (!cancelled) {
        setMemberRoles(result);
        setLoaded(true);
      }
    }
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [unitId, member.id, appBindings]);

  async function toggleRole(clientId: string, roleName: string) {
    const current = memberRoles[clientId] ?? new Set();
    const hasRole = current.has(roleName);

    try {
      if (hasRole) {
        await removeRoles.mutateAsync({ userId: member.id, clientId, roles: [roleName] });
        current.delete(roleName);
      } else {
        await assignRoles.mutateAsync({ userId: member.id, clientId, roles: [roleName] });
        current.add(roleName);
      }
      setMemberRoles({ ...memberRoles, [clientId]: new Set(current) });
      toast.success(hasRole ? `Снята роль ${roleName}` : `Назначена роль ${roleName}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === "ROLES_NOT_PROVISIONED") {
        toast.error(err.message, { duration: 8000 });
      } else {
        toast.error(hasRole ? "Не удалось снять роль" : "Не удалось назначить роль");
      }
    }
  }

  const displayName =
    member.firstName || member.lastName
      ? `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim()
      : member.username;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Роли приложений: {displayName}</DialogTitle>
          <DialogDescription>
            Роли по приложениям определяют, что доступно этому участнику в команде.
          </DialogDescription>
        </DialogHeader>
        {!loaded ? (
          <div className="space-y-2 py-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ) : appBindings.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Для этого типа подразделения нет привязок приложений.
          </p>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div className="space-y-4 py-2">
              {appBindings.map((binding) => (
                <BindingRow
                  key={binding.clientId}
                  binding={binding}
                  active={memberRoles[binding.clientId] ?? new Set()}
                  onToggle={(roleName) => toggleRole(binding.clientId, roleName)}
                  pending={assignRoles.isPending || removeRoles.isPending}
                />
              ))}
            </div>
          </TooltipProvider>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BindingRow({
  binding,
  active,
  onToggle,
  pending,
}: {
  binding: AppRoleBinding;
  active: Set<string>;
  onToggle: (roleName: string) => void;
  pending: boolean;
}) {
  const provisioned = new Set(binding.status.provisionedRoles);
  const reason = binding.status.condition?.reason ?? null;
  const message = binding.status.condition?.message ?? null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-sm font-medium text-foreground">{binding.clientId}</p>
        <Badge
          variant={binding.status.ready ? "ok" : "neutral"}
          className="text-[10px] uppercase tracking-wide"
        >
          {binding.status.ready ? "Готово" : reason ?? "Ожидание"}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        {binding.roles.map((role) => {
          const isProvisioned = provisioned.has(role.name);
          const isActive = active.has(role.name);
          const button = (
            <Button
              key={role.name}
              size="sm"
              variant={isActive ? "primary" : isProvisioned ? "outline" : "ghost"}
              className="text-xs"
              onClick={() => onToggle(role.name)}
              disabled={pending || !isProvisioned}
              title={role.description}
            >
              {role.name}
              {!isProvisioned && (
                <span className="ml-1 text-[10px] text-muted-foreground">·{reason ?? "pending"}</span>
              )}
            </Button>
          );
          if (isProvisioned) {
            return button;
          }
          return (
            <Tooltip key={role.name}>
              <TooltipTrigger asChild>
                <span tabIndex={0}>{button}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  Роль <span className="font-mono">{role.name}</span> объявлена в CRD AppRoleBinding, но пока не появилась в Keycloak.
                </p>
                {message && <p className="text-xs mt-1 text-muted-foreground">{message}</p>}
                <p className="text-xs mt-1 font-mono">
                  kubectl -n keycloak get approlebinding {binding.name}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
